import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { KineticHeading } from "../components/site/KineticHeading";
import { Reveal } from "../components/site/Reveal";
import { StatCounter } from "../components/site/StatCounter";
import { ClientMarquee } from "../components/site/ClientMarquee";
import { EditorialMarquee } from "../components/site/EditorialMarquee";
import { useContent } from "../context/ContentContext";
import { img } from "../lib/api";

const Hero = ({ hero }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={img(hero.image)} alt="Event stage" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-korio-bg" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-20 pt-40 md:px-10">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-volt">
          <span className="h-px w-10 bg-volt" /> {hero.overline}
        </motion.div>

        <KineticHeading
          lines={hero.lines || []}
          className="font-display text-6xl font-semibold uppercase leading-[0.92] tracking-tighter text-white sm:text-7xl lg:text-8xl xl:text-9xl"
        />

        <div className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-xl text-lg text-white/70">
            {hero.paragraph}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="flex flex-shrink-0 flex-wrap gap-4">
            <Link to="/contact" data-testid="hero-start-project" className="brand-gradient group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">
              Start a project <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/products" data-testid="hero-see-products" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-semibold text-white transition-colors duration-300 hover:bg-white/5">
              See the products
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default function Home() {
  const { site } = useContent();
  const hero = site.hero || {};
  const services = site.services || [];
  const stats = site.stats || [];
  const manifesto = site.manifesto || [];

  return (
    <>
      <Hero hero={hero} />

      <section className="border-y border-white/10 bg-korio-bg py-10" data-testid="clients-section">
        <p className="mb-8 text-center text-xs font-bold uppercase tracking-[0.25em] text-white/40">Trusted by 250+ brands worldwide</p>
        <ClientMarquee />
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-36" data-testid="pillars-section">
        <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-volt">What we do</div>
            <Reveal>
              <h2 className="font-display text-4xl font-medium leading-[1] tracking-tight sm:text-5xl lg:text-6xl">
                Three pillars.<br /><span className="text-stroke">Zero filler.</span>
              </h2>
            </Reveal>
          </div>
          <Link to="/services" data-testid="pillars-all-services" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-white/70 transition-colors hover:text-volt">
            All services <ArrowUpRight size={18} />
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.id || i} delay={i * 0.1}>
              <Link to="/services" data-testid={`pillar-card-${s.id || i}`} className="group relative block h-[440px] overflow-hidden rounded-2xl border border-white/10">
                <img src={img(s.image)} alt={s.title} className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-105 group-hover:opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="relative flex h-full flex-col justify-end p-7">
                  <h3 className="font-display text-2xl font-medium tracking-tight text-white">{s.title}</h3>
                  <p className="mt-3 text-sm text-white/60">{s.summary}</p>
                  <span className="mt-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white transition-colors group-hover:border-volt group-hover:bg-volt group-hover:text-black">
                    <ArrowUpRight size={18} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-korio-surface py-24" data-testid="stats-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mb-16 text-xs font-bold uppercase tracking-[0.25em] text-volt">By the numbers</div>
          <Reveal>
            <h2 className="mb-16 font-display text-4xl font-medium tracking-tight sm:text-5xl">
              Loud results. <span className="text-stroke">Quiet confidence.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {stats.map((stat, i) => (
              <Reveal key={stat.label + i} delay={i * 0.1}>
                <div className="border-t border-white/15 pt-6" data-testid={`stat-${i}`}>
                  <div className="font-display text-5xl font-semibold text-gradient sm:text-6xl">
                    <StatCounter value={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="mt-2 text-sm uppercase tracking-widest text-white/50">{stat.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee items={["Registration", "Badging", "Activation", "VR & AI", "Light Painting", "AV Rental"]} />

      <section className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-36" data-testid="manifesto-section">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="sticky top-32">
              <div className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-volt">Our approach</div>
              <h2 className="font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">How we think about your event.</h2>
            </div>
          </div>
          <div className="md:col-span-8">
            {manifesto.map((m, i) => (
              <Reveal key={m.n + i} delay={i * 0.05}>
                <div className="flex gap-8 border-t border-white/10 py-10">
                  <span className="font-mono text-sm text-volt">{m.n}</span>
                  <div>
                    <h3 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">{m.title}</h3>
                    <p className="mt-4 max-w-xl text-lg text-white/60">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-white/10 bg-korio-bg py-28 md:py-40" data-testid="home-cta">
        <div className="mx-auto max-w-[1400px] px-6 text-center md:px-10">
          <Reveal>
            <h2 className="mx-auto max-w-4xl font-display text-5xl font-semibold uppercase leading-[0.95] tracking-tighter sm:text-7xl lg:text-8xl">
              Don't be shy. <span className="text-gradient">Say hello.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto mt-8 max-w-xl text-lg text-white/60">Brief us on your next event and we'll reply within 24 hours with a first read.</p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link to="/contact" data-testid="home-cta-button" className="brand-gradient group mt-10 inline-flex items-center gap-2 rounded-full px-8 py-4 text-lg font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">
              Start a project <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
